import { Injectable } from '@angular/core';
import { take } from 'rxjs/operators';
import * as XLSX from 'xlsx';
import { PriceDiscoveryService } from './price-discovery.service';

const ITEM_HEADERS = new Set(['item', 'item name', 'product', 'product name', 'name', 'description']);
const UNIT_HEADERS = new Set(['unit', 'uom', 'pack', 'packing', 'unit name']);
const QTY_HEADERS = new Set(['quantity', 'qty', 'qnty', 'order qty', 'order quantity']);
const SUPPLIER_TAGS = new Set(['hyperpure', 'hp', 'udaan', 'dmart', 'd-mart', 'd mart', 'deliverit', 'deliver it', 'local']);
const HYPERPURE_TAGS = new Set(['hyperpure', 'hp']);
const MAX_QUANTITY_ROWS = 200;
const SEARCH_CONCURRENCY = 4;
const CART_BATCH_SIZE = 8;

export type HyperpureSheetStatus = 'added' | 'updated' | 'already' | 'not_found' | 'failed' | 'skipped' | 'other_supplier';

export interface HyperpureSheetLine {
    sheetRows: number[];
    item: string;
    unit: string;
    quantity: number | null;
    status: HyperpureSheetStatus;
    detail: string;
}

export interface HyperpureSheetSummary {
    fileName: string;
    lines: HyperpureSheetLine[];
    added: number;
    updated: number;
    already: number;
    notFound: number;
    failed: number;
    skipped: number;
    otherSupplier: number;
    cartChanged: boolean;
}

export interface HyperpureSheetSearchRow {
    item: string;
    unit: string;
    quantity: number | null;
}

export interface HyperpureSheetProgress {
    phase: 'reading' | 'matching' | 'writing';
    done: number;
    total: number;
    label: string;
}

interface ActionRow {
    sheetRows: number[];
    item: string;
    unit: string;
    quantity: number | null;
    searchName: string;
}

interface ParsedSheet {
    actions: ActionRow[];
    lines: HyperpureSheetLine[];
    truncated: boolean;
}

interface MatchedRow extends ActionRow {
    productId: string;
    lookupIds: string[];
    previousQuantity: number;
}

/**
 * Uploads an Item + Unit + Quantity sheet into the Hyperpure cart only.
 * A row is applied when the Hyperpure catalogue has that exact item and unit.
 * The sheet quantity is the target cart quantity: if the cart already has it, the row is left alone.
 */
@Injectable({
    providedIn: 'root'
})
export class HyperpureSheetCartService {

    constructor(private priceDiscoveryService: PriceDiscoveryService) { }

    /**
     * Reads Item, Unit and Quantity rows. Searching happens through Purchase Manager,
     * and the cart quantity is applied later on the price-discovery page.
     */
    async readSheet(file: File): Promise<HyperpureSheetSearchRow[]> {
        const parsed = await this.parseFile(file);
        if (parsed.actions.length === 0) {
            throw new HyperpureSheetStop('No item names were found in this sheet.');
        }
        return parsed.actions.map(row => ({
            item: row.item,
            unit: row.unit,
            quantity: row.quantity
        }));
    }

    async apply(
        file: File,
        entityId: string,
        hooks: { onProgress: (progress: HyperpureSheetProgress) => void; isCancelled: () => boolean }
    ): Promise<HyperpureSheetSummary> {
        hooks.onProgress({ phase: 'reading', done: 0, total: 1, label: 'Reading the sheet' });
        const parsed = await this.parseFile(file);
        const summary = emptySummary(file.name, parsed.lines);

        if (parsed.actions.length === 0) {
            if (!parsed.lines.some(line => line.item)) {
                throw new HyperpureSheetStop('No item rows were found in this sheet.');
            }
            return tally(summary);
        }

        if (hooks.isCancelled()) {
            return tally(summary);
        }

        let cartError: Error | null = null;
        const cartPromise = this.loadCart(entityId).catch(error => {
            cartError = error;
            return new Map<string, number>();
        });
        const searchCache = new Map<string, Promise<any[]>>();
        const matched: MatchedRow[] = [];
        let completed = 0;
        let stopped: Error | null = null;

        hooks.onProgress({
            phase: 'matching',
            done: 0,
            total: parsed.actions.length,
            label: 'Matching items on Hyperpure'
        });

        await mapPool(parsed.actions, SEARCH_CONCURRENCY, async (row) => {
            if (hooks.isCancelled() || stopped) {
                return;
            }
            try {
                const products = await this.searchProducts(row.searchName, entityId, searchCache);
                if (hooks.isCancelled() || stopped) {
                    return;
                }
                const product = findExactProduct(products, row.searchName, row.unit);
                if (!product) {
                    summary.lines.push(lineFrom(row, 'not_found', 'No exact Item + Unit match on Hyperpure'));
                } else if (isOutOfStock(product)) {
                    summary.lines.push(lineFrom(row, 'failed', 'Out of stock on Hyperpure'));
                } else {
                    const productId = cartProductId(product);
                    if (!productId) {
                        summary.lines.push(lineFrom(row, 'failed', 'Hyperpure did not return a product id for this item'));
                    } else {
                        matched.push({
                            ...row,
                            productId,
                            lookupIds: lookupIds(product),
                            previousQuantity: 0
                        });
                    }
                }
            } catch (error) {
                if (error instanceof HyperpureSheetStop) {
                    stopped = error;
                    return;
                }
                summary.lines.push(lineFrom(row, 'failed', httpErrorMessage(error)));
            } finally {
                completed += 1;
                hooks.onProgress({
                    phase: 'matching',
                    done: Math.min(completed, parsed.actions.length),
                    total: parsed.actions.length,
                    label: row.item
                });
            }
        });

        if (stopped) {
            throw stopped;
        }

        if (hooks.isCancelled()) {
            return tally(summary);
        }

        const cart = await cartPromise;
        if (cartError) {
            throw cartError;
        }
        const writes = collapseWrites(matched, cart);

        writes.forEach(write => {
            if (write.previousQuantity === write.quantity) {
                summary.lines.push(lineFrom(write, 'already', `Already ${write.quantity} in the Hyperpure cart`));
            }
        });

        const pending = writes.filter(write => write.previousQuantity !== write.quantity);
        if (pending.length === 0 || hooks.isCancelled()) {
            return tally(summary);
        }

        let written = 0;
        for (let index = 0; index < pending.length; index += CART_BATCH_SIZE) {
            if (hooks.isCancelled()) {
                break;
            }
            const batch = pending.slice(index, index + CART_BATCH_SIZE);
            hooks.onProgress({
                phase: 'writing',
                done: written,
                total: pending.length,
                label: 'Updating the Hyperpure cart'
            });
            let response: any;
            try {
                response = await this.priceDiscoveryService.addMultipleItemsToCart(
                    batch.map(write => ({
                        skuId: numericId(write.productId),
                        quantity: write.quantity,
                        deliveryMode: 'DELIVERY'
                    })),
                    entityId
                ).pipe(take(1)).toPromise();
            } catch (error) {
                const message = isSessionError(error)
                    ? 'Hyperpure session expired before the cart could be updated.'
                    : httpErrorMessage(error);
                batch.forEach(write => summary.lines.push(lineFrom(write, 'failed', message)));
                if (isSessionError(error)) {
                    break;
                }
                written += batch.length;
                continue;
            }

            const failures = indexFailures(response);
            batch.forEach(write => {
                const failure = failures.get(write.productId);
                if (failure) {
                    summary.lines.push(lineFrom(write, 'failed', failure));
                    return;
                }
                if (write.previousQuantity > 0) {
                    summary.lines.push(lineFrom(
                        write,
                        'updated',
                        `Cart had ${write.previousQuantity}, now ${write.quantity}`
                    ));
                } else {
                    summary.lines.push(lineFrom(write, 'added', `Added ${write.quantity} to the Hyperpure cart`));
                }
            });
            written += batch.length;
            summary.cartChanged = summary.cartChanged || batch.some(write => !failures.has(write.productId));
        }

        return tally(summary);
    }

    private async parseFile(file: File): Promise<ParsedSheet> {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        for (const sheetName of workbook.SheetNames) {
            const matrix = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
                header: 1,
                defval: '',
                raw: false
            }) as any[][];
            const parsed = parseMatrix(matrix);
            if (parsed) {
                return parsed;
            }
        }
        throw new HyperpureSheetStop('This sheet needs Item, Unit and Quantity columns.');
    }

    private searchProducts(itemName: string, entityId: string, cache: Map<string, Promise<any[]>>): Promise<any[]> {
        const key = normalizeItemName(itemName);
        const existing = cache.get(key);
        if (existing) {
            return existing;
        }
        const pending = this.priceDiscoveryService.searchHyperpureItems(itemName, entityId)
            .pipe(take(1))
            .toPromise()
            .then(body => extractProducts(body))
            .catch(error => {
                if (isSessionError(error)) {
                    throw new HyperpureSheetStop('Hyperpure session expired. Open Hyperpure once, then upload the sheet again.');
                }
                if (error?.status === 404) {
                    throw new HyperpureSheetStop('Hyperpure is not linked to this outlet.');
                }
                throw error;
            });
        cache.set(key, pending);
        return pending;
    }

    private loadCart(entityId: string): Promise<Map<string, number>> {
        return this.priceDiscoveryService.getCart('', 'DELIVERY', entityId)
            .pipe(take(1))
            .toPromise()
            .then(body => readCartQuantities(body))
            .catch(error => {
                if (isSessionError(error)) {
                    throw new HyperpureSheetStop('Hyperpure session expired. Open Hyperpure once, then upload the sheet again.');
                }
                throw new HyperpureSheetStop('Could not read the Hyperpure cart, so nothing was changed.');
            });
    }
}

export class HyperpureSheetStop extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'HyperpureSheetStop';
    }
}

function emptySummary(fileName: string, lines: HyperpureSheetLine[]): HyperpureSheetSummary {
    return {
        fileName,
        lines: [...lines],
        added: 0,
        updated: 0,
        already: 0,
        notFound: 0,
        failed: 0,
        skipped: 0,
        otherSupplier: 0,
        cartChanged: false
    };
}

function tally(summary: HyperpureSheetSummary): HyperpureSheetSummary {
    summary.added = summary.lines.filter(line => line.status === 'added').length;
    summary.updated = summary.lines.filter(line => line.status === 'updated').length;
    summary.already = summary.lines.filter(line => line.status === 'already').length;
    summary.notFound = summary.lines.filter(line => line.status === 'not_found').length;
    summary.failed = summary.lines.filter(line => line.status === 'failed').length;
    summary.skipped = summary.lines.filter(line => line.status === 'skipped').length;
    summary.otherSupplier = summary.lines.filter(line => line.status === 'other_supplier').length;
    return summary;
}

function lineFrom(row: { sheetRows: number[]; item: string; unit: string; quantity: number | null }, status: HyperpureSheetStatus, detail: string): HyperpureSheetLine {
    const combined = row.sheetRows.length > 1 ? ` Combined ${row.sheetRows.length} sheet rows.` : '';
    return {
        sheetRows: row.sheetRows,
        item: row.item,
        unit: row.unit,
        quantity: row.quantity,
        status,
        detail: `${detail}${combined}`
    };
}

function parseMatrix(matrix: any[][]): ParsedSheet | null {
    let headerAt = -1;
    let columns: { item: number; unit: number; qty: number } | null = null;
    const scanLimit = Math.min(matrix.length, 8);
    for (let index = 0; index < scanLimit; index++) {
        const found = headerColumns(matrix[index] || []);
        if (found) {
            headerAt = index;
            columns = found;
            break;
        }
    }
    if (!columns) {
        return null;
    }

    const grouped = new Map<string, ActionRow>();
    const lines: HyperpureSheetLine[] = [];
    let searchedRows = 0;
    let truncated = false;

    for (let index = headerAt + 1; index < matrix.length; index++) {
        const cells = matrix[index] || [];
        const rawItem = cellText(cells[columns.item]);
        const rawUnit = columns.unit >= 0 ? cellText(cells[columns.unit]) : '';
        const rawQty = columns.qty >= 0 ? cellText(cells[columns.qty]) : '';
        if (!rawItem && !rawUnit && !rawQty) {
            continue;
        }
        const sheetRow = index + 1;
        const tagged = splitSupplierTag(rawItem);
        if (!tagged.name) {
            lines.push({
                sheetRows: [sheetRow],
                item: rawItem || 'Blank item',
                unit: rawUnit,
                quantity: null,
                status: 'skipped',
                detail: 'Item name is empty'
            });
            continue;
        }
        if (tagged.supplier && !HYPERPURE_TAGS.has(tagged.supplier)) {
            lines.push({
                sheetRows: [sheetRow],
                item: tagged.name,
                unit: rawUnit,
                quantity: null,
                status: 'other_supplier',
                detail: 'Only Hyperpure rows are added to the cart'
            });
            continue;
        }

        const parsedQuantity = parseQuantity(rawQty);
        const quantity = typeof parsedQuantity === 'number' ? parsedQuantity : null;

        searchedRows += 1;
        if (searchedRows > MAX_QUANTITY_ROWS) {
            truncated = true;
            break;
        }

        const key = `${normalizeItemName(tagged.name)}|${normalizeUnit(rawUnit)}`;
        const existing = grouped.get(key);
        if (existing) {
            existing.sheetRows.push(sheetRow);
            if (quantity != null) {
                existing.quantity = (existing.quantity || 0) + quantity;
            }
        } else {
            grouped.set(key, {
                sheetRows: [sheetRow],
                item: tagged.name,
                unit: rawUnit,
                quantity,
                searchName: tagged.name
            });
        }
    }

    if (truncated) {
        lines.push({
            sheetRows: [],
            item: '',
            unit: '',
            quantity: null,
            status: 'skipped',
            detail: `Only the first ${MAX_QUANTITY_ROWS} items were searched`
        });
    }

    return { actions: Array.from(grouped.values()), lines, truncated };
}

function headerColumns(row: any[]): { item: number; unit: number; qty: number } | null {
    const headers = row.map(cell => normalizeHeader(cellText(cell)));
    const item = headers.findIndex(header => ITEM_HEADERS.has(header));
    const unit = headers.findIndex(header => UNIT_HEADERS.has(header));
    if (item === -1) {
        return null;
    }
    return {
        item,
        unit,
        qty: headers.findIndex(header => QTY_HEADERS.has(header))
    };
}

function parseQuantity(value: string): number | null | undefined {
    const cleaned = value.replace(/,/g, '').trim();
    if (!cleaned) {
        return null;
    }
    const quantity = Number(cleaned);
    if (!Number.isFinite(quantity) || quantity <= 0 || quantity > 100000 || !Number.isInteger(quantity)) {
        return undefined;
    }
    return quantity;
}

function splitSupplierTag(raw: string): { name: string; supplier: string | null } {
    const trimmed = raw.replace(/\s+/g, ' ').trim();
    const match = trimmed.match(/^(.*)\(([^)]+)\)\s*$/);
    if (!match) {
        return { name: trimmed, supplier: null };
    }
    const supplier = match[2].trim().toLowerCase();
    if (!SUPPLIER_TAGS.has(supplier)) {
        return { name: trimmed, supplier: null };
    }
    return {
        name: match[1].replace(/\s+/g, ' ').trim(),
        supplier
    };
}

export function encodePmSearchTag(item: { name: string; sheetItemName?: string; unit?: string; targetQuantity?: number | null; sheetRun?: number; fromSheet?: boolean }): string {
    const itemName = (item.sheetItemName || item.name || '').trim();
    const unit = (item.unit || '').trim();
    const quantity = item.targetQuantity && item.targetQuantity > 0 ? String(item.targetQuantity) : '';
    const fromSheet = item.fromSheet || item.sheetRun != null || !!item.sheetItemName;
    if (!fromSheet) {
        return itemName;
    }
    return `${itemName}$$|$$${unit}$$|$$${quantity}$$|$$sheet`;
}

export function decodePmSearchTag(tag: string): { name: string; unit?: string; targetQuantity?: number; fromSheet: boolean } {
    const parts = String(tag || '').split('$$|$$');
    const markedSheet = parts.length >= 4 && parts[parts.length - 1] === 'sheet';
    if (markedSheet || parts.length >= 3) {
        const quantityIndex = markedSheet ? parts.length - 2 : parts.length - 1;
        const unitIndex = quantityIndex - 1;
        const quantity = Number(parts[quantityIndex]);
        const unit = (parts[unitIndex] || '').trim();
        const name = parts.slice(0, unitIndex).join('$$|$$').trim();
        if (name) {
            return {
                name,
                unit: unit || undefined,
                targetQuantity: Number.isInteger(quantity) && quantity > 0 ? quantity : undefined,
                fromSheet: true
            };
        }
    }
    return { name: tag, fromSheet: false };
}

/** Names at or above this score are treated as the same item. */
export const NAME_MATCH_THRESHOLD = 0.8;

/**
 * 1 when the names are the same, otherwise the stronger of character similarity and shared words.
 */
export function nameSimilarity(left: string, right: string): number {
    const a = comparableName(left);
    const b = comparableName(right);
    if (!a || !b) {
        return 0;
    }
    if (a === b) {
        return 1;
    }
    return Math.max(levenshteinRatio(a, b), tokenDice(a, b));
}

function comparableName(value: string): string {
    return normalizeItemName(value).replace(/[^a-z0-9]+/g, ' ').trim();
}

function levenshteinRatio(a: string, b: string): number {
    const maxLen = Math.max(a.length, b.length);
    if (maxLen === 0) {
        return 1;
    }
    return (maxLen - levenshtein(a, b)) / maxLen;
}

function levenshtein(a: string, b: string): number {
    const previous = new Array(b.length + 1);
    const current = new Array(b.length + 1);
    for (let column = 0; column <= b.length; column++) {
        previous[column] = column;
    }
    for (let row = 1; row <= a.length; row++) {
        current[0] = row;
        for (let column = 1; column <= b.length; column++) {
            const cost = a[row - 1] === b[column - 1] ? 0 : 1;
            current[column] = Math.min(
                previous[column] + 1,
                current[column - 1] + 1,
                previous[column - 1] + cost
            );
        }
        for (let column = 0; column <= b.length; column++) {
            previous[column] = current[column];
        }
    }
    return previous[b.length];
}

function tokenDice(a: string, b: string): number {
    const left = a.split(' ').filter(Boolean);
    const right = b.split(' ').filter(Boolean);
    if (left.length === 0 || right.length === 0) {
        return 0;
    }
    const rightTokens = new Set(right);
    let overlap = 0;
    left.forEach(token => {
        if (rightTokens.has(token)) {
            overlap += 1;
        }
    });
    return (2 * overlap) / (left.length + right.length);
}

export function normalizeItemName(value: string): string {
    return String(value || '')
        .replace(/\(\s*hyperpure\s*\)/ig, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

export function normalizeUnit(value: string): string {
    let unit = String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    unit = unit.replace(/^(?:1|a|an|per)\s+/, '');
    const aliases: { [key: string]: string } = {
        pc: 'piece',
        pcs: 'piece',
        piece: 'piece',
        pieces: 'piece',
        pkt: 'packet',
        pkts: 'packet',
        packet: 'packet',
        packets: 'packet',
        btl: 'bottle',
        btls: 'bottle',
        bottle: 'bottle',
        bottles: 'bottle',
        can: 'can',
        cans: 'can'
    };
    if (aliases[unit]) {
        return aliases[unit];
    }
    const packOf = unit.match(/^(?:pack|packet|pkt)\s+of\s+(\d+)$/);
    if (packOf) {
        return `pack of ${packOf[1]}`;
    }
    return unit;
}

function normalizeHeader(value: string): string {
    return value
        .replace(/^\uFEFF/, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function cellText(value: any): string {
    return value == null ? '' : String(value).trim();
}

function findExactProduct(products: any[], itemName: string, unit: string): any | null {
    const nameKey = normalizeItemName(itemName);
    const unitKey = normalizeUnit(unit);
    if (!nameKey || !unitKey) {
        return null;
    }
    const matches = products.filter(product => {
        const names = [product?.productName, product?.itemName, product?.name, product?.displayName]
            .filter(Boolean)
            .map((name: string) => normalizeItemName(name));
        if (names.indexOf(nameKey) === -1) {
            return false;
        }
        return orderUnits(product).some(candidate => normalizeUnit(candidate) === unitKey);
    });
    if (matches.length === 0) {
        return null;
    }
    return matches.find(product => !isOutOfStock(product)) || matches[0];
}

function orderUnits(product: any): string[] {
    const units: string[] = [];
    if (product?.uom) {
        units.push(String(product.uom));
    }
    if (product?.unit && String(product.unit) !== String(product?.uom || '')) {
        units.push(String(product.unit));
    }
    const count = Number(product?.subUomCount || 0);
    if (count > 1) {
        units.push(`pack of ${count}`);
    }
    if (units.length === 0 && product?.subUom) {
        units.push(String(product.subUom));
    }
    return units;
}

function isOutOfStock(product: any): boolean {
    return product?.isInStock === false || product?.inStock === false;
}

function cartProductId(product: any): string {
    const raw = product?.productId ?? product?.skuId ?? product?.id ?? '';
    return raw == null ? '' : String(raw).trim();
}

function lookupIds(product: any): string[] {
    const ids = [product?.productId, product?.skuId, product?.id, product?.productNumber];
    const unique: string[] = [];
    ids.forEach(id => {
        const key = id == null ? '' : String(id).trim();
        if (key && unique.indexOf(key) === -1) {
            unique.push(key);
        }
    });
    return unique;
}

function numericId(productId: string): number | string {
    return /^\d+$/.test(productId) ? Number(productId) : productId;
}

function extractProducts(body: any, depth = 0): any[] {
    if (!body || depth > 4) {
        return [];
    }
    if (Array.isArray(body)) {
        return body.map(unwrapProduct);
    }
    const direct = body.products || body.catalogProducts || body.searchResults || body.items;
    if (Array.isArray(direct)) {
        return direct.map(unwrapProduct);
    }
    if (Array.isArray(body.categories)) {
        return body.categories.reduce((all: any[], category: any) => all.concat(extractProducts(category, depth + 1)), []);
    }
    if (body.response) {
        return extractProducts(body.response, depth + 1);
    }
    if (body.data && body.data !== body) {
        return extractProducts(body.data, depth + 1);
    }
    return [];
}

function unwrapProduct(entry: any): any {
    return entry?.productDetails || entry?.product || entry;
}

export function readCartQuantities(body: any): Map<string, number> {
    const products = extractCartProducts(body);
    const quantities = new Map<string, number>();
    products.forEach(item => {
        const details = item?.productDetails || item?.product || {};
        const quantity = Number(item?.quantity ?? item?.selectedQuantity ?? 0) || 0;
        [details.productId, details.productNumber, details.skuId, item?.productId, item?.skuId].forEach(id => {
            if (id != null && String(id).trim() !== '') {
                quantities.set(String(id), quantity);
            }
        });
    });
    return quantities;
}

function extractCartProducts(body: any): any[] {
    if (!body) {
        return [];
    }
    const buckets = [
        body.response?.cartProducts,
        body.cartProducts,
        body.response?.products,
        body.products
    ];
    for (const bucket of buckets) {
        if (Array.isArray(bucket)) {
            return bucket;
        }
    }
    return [];
}

function quantityInCart(cart: Map<string, number>, ids: string[]): number {
    for (const id of ids) {
        if (cart.has(id)) {
            return cart.get(id) || 0;
        }
    }
    return 0;
}

function collapseWrites(rows: MatchedRow[], cart: Map<string, number>): MatchedRow[] {
    const grouped = new Map<string, MatchedRow>();
    rows.forEach(row => {
        const existing = grouped.get(row.productId);
        if (existing) {
            existing.quantity = (existing.quantity || 0) + (row.quantity || 0);
            existing.sheetRows = existing.sheetRows.concat(row.sheetRows);
            row.lookupIds.forEach(id => {
                if (existing.lookupIds.indexOf(id) === -1) {
                    existing.lookupIds.push(id);
                }
            });
            existing.previousQuantity = quantityInCart(cart, existing.lookupIds);
            return;
        }
        grouped.set(row.productId, {
            ...row,
            sheetRows: [...row.sheetRows],
            lookupIds: [...row.lookupIds],
            previousQuantity: quantityInCart(cart, row.lookupIds)
        });
    });
    return Array.from(grouped.values());
}

function indexFailures(response: any): Map<string, string> {
    const failures = new Map<string, string>();
    const errors = response?.errors || [];
    if (!Array.isArray(errors)) {
        return failures;
    }
    errors.forEach((error: any) => {
        const id = error?.skuId ?? error?.sku_id ?? error?.productId;
        if (id == null) {
            return;
        }
        failures.set(String(id), errorText(error?.error || error?.message));
    });
    return failures;
}

function errorText(error: any): string {
    if (!error) {
        return 'Could not update the Hyperpure cart';
    }
    if (typeof error === 'string') {
        return error;
    }
    return error.message || error.error || 'Could not update the Hyperpure cart';
}

function httpErrorMessage(error: any): string {
    return error?.error?.error || error?.error?.message || error?.message || 'Search failed';
}

function isSessionError(error: any): boolean {
    const message = httpErrorMessage(error).toLowerCase();
    return error?.status === 401 || message.includes('session') || message.includes('signed-in') || message.includes('signed in');
}

async function mapPool<T>(items: T[], limit: number, worker: (item: T) => Promise<void>): Promise<void> {
    if (items.length === 0) {
        return;
    }
    let cursor = 0;
    const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
        while (cursor < items.length) {
            const index = cursor;
            cursor += 1;
            await worker(items[index]);
        }
    });
    await Promise.all(runners);
}
